import * as React from 'react';
import equal from 'fast-deep-equal/react';
import styles from './NotificationSpecial.scss';
import classNames from 'classnames';
import CloseButton from '~/components/CloseButton/CloseButton';
import { updateViewNotification } from '~/Actions/ActionAccount';
import { changeVisiblePopup, setCurrentNotificationName, setVisibleInfoScreen } from '~/Actions/ActionApp';
import dwhExport from '~/api/dwhExport';
import { DWH_EVENTS } from '~/const';
import { useDispatch, useSelector } from 'react-redux';
import { markNotificationAsViewed } from '~/api/account';
import { State } from '~/Reducers';
import { POPUPS_NAME } from '~/components/PopupManager';
import useClickAway from '~/hooks/useClickAway';
import useKeyDown, { KEYS_CODE } from '~/hooks/useKeyDown';
import { redirectTo } from '~/utils/notifications';
import { updateBrowserControlState } from '@wg/web2clientapi/browser/updateBrowserControlState';
import { NotificationBannerPosition } from '~/types/notifications';
import { playButtonClickSound } from '~/api/WoWsClient';
import { Button, OrangeButton } from '~/components/Button/Buttons';
import { t } from '~/utils/localization';
import { ICurrentPage } from '~/Actions/ActionAppType';

interface INotificationSpecial {
    notification: INotification;
}

interface IStateSelector {
    accountId: number;
    viewedNotifications: string[];
    currentPage: ICurrentPage;
}

const stateSelector = (state: State): IStateSelector => {
    return {
        accountId: state.ReducerAccount.id,
        viewedNotifications: state.ReducerAccount.viewedNotifications,
        currentPage: state.ReducerApp.currentPage,
    };
};

const NotificationSpecial = ({ notification }: INotificationSpecial) => {
    const ref = React.useRef(null);
    const dispatch = useDispatch();
    const state = useSelector<State, IStateSelector>(stateSelector, equal);
    const isFullscreen = notification.bannerPosition === NotificationBannerPosition.FULLSCREEN;
    const isCenter = notification.bannerPosition === NotificationBannerPosition.CENTER;
    const classesWrapper = classNames(styles.wrapper, {
        [styles.left]: notification.bannerPosition === NotificationBannerPosition.LEFT,
        [styles.right]: notification.bannerPosition === NotificationBannerPosition.RIGHT,
        [styles.center]: isCenter,
        [styles.fullscreen]: isFullscreen,
    });

    const close = (isDwhSendEvent = false) => {
        dispatch(updateViewNotification(notification.name));
        dispatch(setCurrentNotificationName(null));
        dispatch(changeVisiblePopup(null));

        if (isDwhSendEvent) {
            dwhExport.send(DWH_EVENTS.CLOSE_NOTIFICATION, { notification_name: notification.name });
        }

        if (state.accountId && !state.viewedNotifications?.includes(notification.name)) {
            markNotificationAsViewed(notification.name);
        }
    };

    const onClick = () => {
        close();
        dwhExport.send(DWH_EVENTS.OPEN_CATEGORY_FROM_NOTIFICATION, { notification_name: notification.name });
        if (notification) {
            redirectTo(notification);
        }
    };

    const setRef = (_ref: HTMLDivElement) => {
        if (_ref) {
            ref.current = _ref;
        }
    };

    const [textPositionX, textPositionY] = notification.textPosition || [];

    const styleInfo = !isFullscreen
        ? {
              left: textPositionX + '%',
              top: textPositionY + '%',
          }
        : {};

    const style = {
        backgroundImage: `url(${notification.background})`,
    };

    React.useEffect(() => {
        if (isFullscreen) {
            updateBrowserControlState(true);
        }
    }, [notification.name]);

    React.useEffect(() => {
        dispatch(setVisibleInfoScreen(true, notification.name));
    }, []);

    useClickAway(
        ref,
        () => {
            if (isCenter) {
                close();
            }
        },
        [notification.name],
    );

    useKeyDown(() => {
        if (isFullscreen) {
            playButtonClickSound();
            close();
        }
    }, [KEYS_CODE.ESC]);

    const isNeedToShownRedirectButton = (notification.redirectToCategory && state.currentPage?.name !== notification.redirectToCategory) || notification.linkHref;

    return (
        <div className={classesWrapper}>
            <div
                style={style}
                onClick={() => {
                    !isFullscreen && onClick();
                }}
                key={`notification_${notification.name}`}
                className={classNames(styles.content, styles[notification.size])}
            >
                <div className={styles.container} ref={setRef}>
                    {!isFullscreen && (
                        <div className={styles.close}>
                            <CloseButton
                                onClick={(event) => {
                                    event.stopPropagation();
                                    close(true);
                                }}
                            />
                        </div>
                    )}
                    <div className={styles.info} style={styleInfo}>
                        <div className={styles.title}>{notification.title}</div>
                        <div className={styles.description}>{notification.description}</div>
                        {isFullscreen && (
                            <div className={styles.buttons}>
                                {isNeedToShownRedirectButton && <OrangeButton label={t('Перейти')} onClick={onClick} />}
                                <Button label={t('Закрыть')} onClick={() => close(true)} className={styles.closeButton} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationSpecial;
